// src/pages/ServiceDetail/components/LogsTab.tsx

import React, { useState } from 'react';
import { ReloadOutlined, DownloadOutlined, SyncOutlined } from '@ant-design/icons';
import { AnyService } from '../../../types/service.type';

interface LogEntry {
  id: string;
  timestamp: Date;
  level: 'info' | 'warning' | 'error' | 'debug';
  message: string;
  source?: string;
}

interface LogsTabProps {
  service: AnyService;
}

const LogsTab: React.FC<LogsTabProps> = ({ service }) => {
  const [logType, setLogType] = useState<string>('application');
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Mock logs
  const logs: LogEntry[] = [
    {
      id: '1',
      timestamp: new Date('2024-12-02T10:30:15'),
      level: 'error',
      message: 'Database connection failed: Connection timeout after 30s',
      source: 'database.php',
    },
    {
      id: '2',
      timestamp: new Date('2024-12-02T10:30:10'),
      level: 'info',
      message: 'Starting deployment process for version 2.1.5',
      source: 'deploy.sh',
    },
    {
      id: '3',
      timestamp: new Date('2024-12-02T10:29:45'),
      level: 'warning',
      message: 'Memory usage at 85%, consider scaling',
      source: 'monitor.js',
    },
    {
      id: '4',
      timestamp: new Date('2024-12-02T10:29:30'),
      level: 'info',
      message: 'Processing request: GET /api/posts',
      source: 'server.js',
    },
    {
      id: '5',
      timestamp: new Date('2024-12-02T10:29:15'),
      level: 'debug',
      message: 'Cache hit for key: homepage_data',
      source: 'cache.js',
    },
    {
      id: '6',
      timestamp: new Date('2024-12-02T10:29:00'),
      level: 'info',
      message: 'User authentication successful',
      source: 'auth.php',
    },
  ];

  const logStats = {
    info: 142,
    warning: 23,
    error: 5,
    debug: 89,
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error':
        return 'text-red-400';
      case 'warning':
        return 'text-yellow-400';
      case 'info':
        return 'text-blue-400';
      case 'debug':
        return 'text-gray-400';
      default:
        return 'text-gray-400';
    }
  };

  const getLevelLabel = (level: string) => {
    return level.toUpperCase().padEnd(7);
  };

  const handleDownload = () => {
    const logContent = logs
      .map(
        (log) =>
          `[${log.timestamp.toISOString()}] [${log.level.toUpperCase()}] ${log.message}`
      )
      .join('\n');
    const blob = new Blob([logContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${service.name}-logs-${Date.now()}.txt`;
    a.click();
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <select
            value={logType}
            onChange={(e) => setLogType(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white dark:bg-gray-800 dark:text-white"
          >
            <option value="application">Application Logs</option>
            <option value="server">Server Logs</option>
            <option value="access">Access Logs</option>
            <option value="error">Error Logs</option>
          </select>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 text-black focus:ring-black"
            />
            <SyncOutlined className={autoRefresh ? 'animate-spin' : ''} />
            Auto-refresh
          </label>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => console.log('Refresh logs')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors dark:text-gray-300"
          >
            <ReloadOutlined />
            Refresh
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 bg-black text-white dark:bg-white dark:text-black rounded-lg text-sm hover:bg-dark-bg transition-colors"
          >
            <DownloadOutlined />
            Download
          </button>
        </div>
      </div>

      {/* Log Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-2xl font-bold text-blue-700">{logStats.info}</p>
          <p className="text-xs text-blue-600 mt-1">Info</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-2xl font-bold text-yellow-700">{logStats.warning}</p>
          <p className="text-xs text-yellow-600 mt-1">Warnings</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-2xl font-bold text-red-700">{logStats.error}</p>
          <p className="text-xs text-red-600 mt-1">Errors</p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{logStats.debug}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Debug</p>
        </div>
      </div>

      {/* Logs Display */}
      <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm overflow-auto max-h-[500px]">
        <div className="space-y-1">
          {logs.map((log) => (
            <div key={log.id} className="flex gap-3 text-gray-300 hover:bg-dark-bg px-2 py-1 rounded">
              <span className="text-gray-500 flex-shrink-0">
                {log.timestamp.toLocaleTimeString()}
              </span>
              <span className={`flex-shrink-0 font-semibold ${getLevelColor(log.level)}`}>
                [{getLevelLabel(log.level)}]
              </span>
              <span className="flex-1">{log.message}</span>
              {log.source && (
                <span className="text-gray-600 flex-shrink-0 text-xs">
                  {log.source}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer Info */}
      <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
        <p>
          Showing last {logs.length} entries • Last updated:{' '}
          {new Date().toLocaleTimeString()}
        </p>
        <p>Log file size: 2.4 MB</p>
      </div>
    </div>
  );
};

export default LogsTab;